import { useState, useRef, useEffect } from 'react';
import { Settings as SettingsIcon, Shield, ChevronDown } from 'lucide-react';
import { useAdmin } from '../lib/AdminContext';
import { useApp } from '../lib/AppContext';

interface ProfileMenuProps {
  onSettingsClick: () => void;
}

export default function ProfileMenu({ onSettingsClick }: ProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { isAdmin, enterAdminMode, viewAsUser } = useAdmin();
  const { navigateTo } = useApp();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAdminModeClick = () => {
    enterAdminMode('profile');
    navigateTo('admin-analytics');
    setIsOpen(false);
  };

  const handleSettingsClick = () => {
    onSettingsClick();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 pl-3 pr-4 py-1 rounded-xl bg-[#181818] hover:bg-[#1A1A1A] border border-[#282828] transition-colors"
      >
        <SettingsIcon className="w-5 h-5 text-[#F5F5F5]" />
        <ChevronDown
          className={`w-4 h-4 text-[#A0A0A0] transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-56 bg-[#181818] border border-[#282828] rounded-xl shadow-2xl overflow-hidden z-50"
          style={{
            boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16)',
          }}
        >
          <button
            onClick={handleSettingsClick}
            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#1A1A1A] transition-colors text-left border-b border-[#282828]"
          >
            <SettingsIcon className="w-4 h-4 text-[#A0A0A0]" />
            <span className="text-[14px] text-[#F5F5F5]">Settings</span>
          </button>

          {isAdmin && !viewAsUser && (
            <button
              onClick={handleAdminModeClick}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#1A1A1A] transition-colors text-left"
            >
              <Shield className="w-4 h-4 text-[#FFB86C] flex-shrink-0" />
              <span className="text-[14px] text-[#FFB86C] whitespace-nowrap overflow-hidden text-ellipsis">Admin Workspace</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
